import React, { useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Collapse,
  useTheme,
  Grid,
} from "@mui/material";
import { getFormSectionStyles } from "../formSectionStyles";
import DynamicServiceForm from "../DynamicServiceForm";
import { getStepDefinitionMap, getStepDefinitions } from "../stepDefinitions";

const SYNDICATION_STEPS = getStepDefinitions("syndication");
const SYNDICATION_STEP_MAP = getStepDefinitionMap("syndication");
const SERVICE_OPTIONS = SYNDICATION_STEPS.map((s) => s.id);

interface AssetSyndicationFormProps {
  config: any;
  handleChange: (path: string, value: any) => void;
}

const AssetSyndicationForm: React.FC<AssetSyndicationFormProps> = ({
  config,
  handleChange,
}) => {
  const theme = useTheme();
  const styles = getFormSectionStyles(theme);
  const cachedConfigsRef = useRef<Record<string, any>>({});
  const disabledServicesRef = useRef<Set<string>>(new Set());

  const enabledServices = useMemo(() => {
    return SERVICE_OPTIONS.filter(
      (s) => config[`assetSyndication_${s}_serviceConfig`]
    );
  }, [config]);

  const toggleService = (service: string, enabled: boolean) => {
    const serviceKey = `assetSyndication_${service}_serviceConfig`;
    const definition = SYNDICATION_STEP_MAP[service];

    let flowSteps: string[] = [];
    const fc = config.flowControl_assetSyndication_steps;
    if (Array.isArray(fc)) flowSteps = [...fc];
    else if (typeof fc === "object" && fc !== null)
      flowSteps = Object.values(fc).map(String);

    if (enabled) {
      disabledServicesRef.current.delete(service);
      (definition?.stepFlags || []).forEach((flag) => {
        if (!flowSteps.includes(flag.key)) flowSteps.push(flag.key);
      });
      const restored =
        cachedConfigsRef.current[service] ||
        structuredClone(definition?.defaults || {});
      handleChange(serviceKey, restored);
      cachedConfigsRef.current[service] = restored;
    } else {
      disabledServicesRef.current.add(service);
      cachedConfigsRef.current[service] = config[serviceKey];
      const keys = new Set((definition?.stepFlags || []).map((f) => f.key));
      flowSteps = flowSteps.filter((s) => !keys.has(s));
      handleChange(serviceKey, undefined);
    }

    handleChange("flowControl_assetSyndication_steps", flowSteps);
  };

  return (
    <Paper sx={styles.paper}>
      <Typography variant="h6" sx={styles.title}>
        Asset Syndication Services
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {SERVICE_OPTIONS.map((service) => {
          const enabled = enabledServices.includes(service);
          const definition = SYNDICATION_STEP_MAP[service];
          return (
            <Grid item xs="auto" key={service}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enabled}
                    onChange={(e) => toggleService(service, e.target.checked)}
                    sx={styles.checkbox}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 500 }}>
                    {definition?.label ||
                      service.charAt(0).toUpperCase() + service.slice(1)}
                  </Typography>
                }
              />
            </Grid>
          );
        })}
      </Grid>

      {SERVICE_OPTIONS.map((service) => {
        const serviceKey = `assetSyndication_${service}_serviceConfig`;
        const enabled = enabledServices.includes(service);
        const definition = SYNDICATION_STEP_MAP[service];
        return (
          <Collapse in={enabled} unmountOnExit key={service}>
            <Box sx={styles.nestedSection}>
              {definition?.renderer === "dynamic" ? (
                <DynamicServiceForm
                  definition={definition}
                  config={config[serviceKey] || {}}
                  onChange={(field: string, value: any) => {
                    if (disabledServicesRef.current.has(service)) return;
                    handleChange(`${serviceKey}.${field}`, value);
                  }}
                />
              ) : null}
            </Box>
          </Collapse>
        );
      })}
    </Paper>
  );
};

export default AssetSyndicationForm;
